import {trans} from '@frontend/helpers'
import {ChatsApi} from '@frontend/modules/private/chats/ChatsApi'
import type {ChatMessageData} from '@frontend/modules/private/chats/ChatsModuleTypes'
import type {AnyObject} from '@frontend/types/AppTypes'
import {useAutogrowOnTextArea} from '@shared/helpers/useAutogrowOnTextArea'
import {
    type ChangeEvent,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {Modal} from 'swayok-react-mdb-ui-kit/components/Modal/Modal'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalContent} from 'swayok-react-mdb-ui-kit/components/Modal/ModalContent'
import {ModalFooter} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooter'
import {ModalFooterCloseButton} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooterCloseButton'
import {ModalHeader} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeader'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'

interface Props {
    show: boolean | null
    depth?: number
    executorId: number
    onClose: () => void
    onClosed?: () => void
    onMessageSent: (chatId: number) => void
}

// Модальное окно отправки сообщения исполнителю услуг.
export function ExecutorDetailsSendMessageModal(props: Props) {

    const {
        show,
        depth,
        executorId,
        onClose,
        onClosed,
        onMessageSent,
    } = props

    // Сообщение.
    const [
        messageText,
        setMessageText,
    ] = useState<string>('')

    // Состояние отправки сообщения.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Работа с авто-высотой текстового поля ввода.
    const {
        inputRefHandler,
        inputElement,
    } = useAutogrowOnTextArea({
        value: messageText,
    })

    // Обработка события начала открытия модального окна.
    const onShow = useEventCallback((): void => {
        setTimeout(() => {
            inputElement?.focus()
        }, 200)
    })

    // Отправить сообщение.
    const sendMessage = useEventCallback((): void => {
        if (isProcessing || messageText.trim() === '') {
            return
        }
        setIsProcessing(true)
        ChatsApi.addMessageToChat(messageText.trim(), null, null, executorId)
            .then((messageData: ChatMessageData): void => {
                ToastService.success(trans().services.messages.chat_message_sent)
                onMessageSent(messageData.chatId)
                setMessageText('')
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error, {
                    suppressValidationErrorMessage: true,
                    validationErrors(errors: AnyObject<string>): void {
                        const messages: string[] = Object.values(errors)
                        ToastService.error(
                            messages.join('; ').replace(/\.;/g, ';'),
                            messages.length > 2 ? 8000 : 5000
                        )
                    },
                })
            })
            .finally(() => {
                setIsProcessing(false)
            })
    })

    return (
        <Modal
            show={show}
            depth={depth}
            onClose={onClose}
            onClosed={onClosed}
            onShow={onShow}
            size="md"
            staticBackdrop
            closeOnEsc
        >
            <ModalContent>
                <ModalHeader
                    title={trans().services.details.send_message_modal.header}
                    onClose={onClose}
                />
                <ModalBody>
                    <form
                        className="m-0 flex-1"
                        onSubmit={event => {
                            event.preventDefault()
                            sendMessage()
                        }}
                    >
                        <Input
                            textarea
                            label={trans().ads.details.send_message_modal.message}
                            value={messageText}
                            wrapperClassName="m-0"
                            className="autogrow"
                            style={{
                                minHeight: 110,
                                maxHeight: 290,
                            }}
                            disabled={isProcessing}
                            inputRef={inputRefHandler}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                                setMessageText(event.currentTarget.value)
                            }}
                            onBlur={() => {
                                setMessageText(messageText.trim())
                            }}
                        />
                    </form>
                </ModalBody>
                <ModalFooter flexBetween>
                    <ModalFooterCloseButton
                        title={trans().common.modal.cancel}
                        className="d-none d-sm-block"
                        onClose={onClose}
                    />
                    <Button
                        color="green"
                        className="full-width-sm-down"
                        disabled={isProcessing || messageText.trim() === ''}
                        onClick={sendMessage}
                    >
                        {trans().ads.details.send_message_modal.submit}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}
